import { createSelector } from 'reselect';
import {
  iterator as iteratorSelector,
  tree as treeSelector,
  alphaSelector,
} from '../AdvWorkflow/AdvWorkflowSelector';
import {
  advance,
  isVisible as areIteratorsEqual,
  ITERATOR_POST_END,
  ITERATOR_PRE_BEGIN,
} from '../AdvWorkflow/AdvWorkflowHelpers';
import { isEmpty } from '../../../js/app/react/validators';
import { WORKFLOW_ID as FINANCIAL_PLAN_WORKFLOW_ID } from '../FinancialAccountsWorkflow/FinancialAccountsWorkflowConstants';
import { WORKFLOW_ID as EDUCATION_WORKFLOW_ID } from '../EducationWorkflow/EducationWorkflowConstants';
import { WORKFLOW_ID as RETIREMENT_WORKFLOW_ID } from '../RetirementWorkflow/RetirementWorkflowConstants';
import { WORKFLOW_ID as DEBT_MANAGEMENT_WORKFLOW_ID } from '../DebtManagementWorkflow/DebtManagementWorkflowConstants';
import { WORKFLOW_ID as HOME_PURCHASE_WORKFLOW_ID } from '../HomePurchaseWorkflow/HomePurchaseWorkflowConstants';
import { WORKFLOW_ID as EMERGENCY_FUND_WORKFLOW_ID } from '../EmergencyFundWorkflow/EmergencyFundWorkflowConstants';
import { WORKFLOW_ID as LARGE_PURCHASE_WORKFLOW_ID } from '../LargePurchaseWorkflow/LargePurchaseWorkflowConstants';

import { viewIdSelector as accountManualViewIdSelector } from '../FinancialAccountsWorkflow/Steps/AccountsManualStepSelector';
import { viewIdSelector as accountViewIdSelector } from '../AccountsWorkflow/Steps/AccountsStep/AccountsStepSelector';
import { viewIDSelector as childrenViewIdSelector } from '../ChildrenInfoWorkflow/Steps/ChildrenCRUDSelector';
import { viewIDSelector as pensionsViewIdSelector } from '../PensionWorkflow/Steps/PensionsOverviewSelector';
import { viewIdSelector as debtsViewIdSelector } from '../DebtManagementDebtsWorkflow/Steps/AccountsStep/AccountsStepSelector';

import { ADD, EDIT } from '../FinancialAccountsWorkflow/Steps/AccountsManualStepViews';
import { CHILDREN_CRUD_ADD_EDIT } from '../PersonalInfoWorkflow/PersonalInfoWorkflowConstants';
import { PENSIONS_CRUD_ADD_EDIT } from '../PensionWorkflow/PensionWorkflowConstants';

import { currentViewSelector as upsertViewSelector } from '../../UpsertAccountModal/UpsertAccountModalSelector';
import { VIEWS as UPSERT_VIEWS } from '../../UpsertAccountModal/UpsertAccountModalConstants';

const workfowIdSelector = (_, { workflowId }) => workflowId;

export const numberOfStepsSelector = createSelector(treeSelector, tree => {
  if (isEmpty(tree)) {
    return 1;
  }
  let iterator = ITERATOR_PRE_BEGIN;
  let count = 0;
  while (iterator !== ITERATOR_POST_END) {
    iterator = advance(1, tree, iterator);
    count += 1;
  }
  return count - 1;
});

export const currentStepSelector = createSelector(
  treeSelector,
  iteratorSelector,
  (tree, currentPosition) => {
    if (isEmpty(tree)) {
      return 0;
    }
    let iterator = ITERATOR_PRE_BEGIN;
    let count = 0;
    while (!areIteratorsEqual(iterator, currentPosition)) {
      iterator = advance(1, tree, iterator);
      count += 1;
    }
    return count - 1;
  },
);

export const percentageSelector = createSelector(
  numberOfStepsSelector,
  currentStepSelector,
  alphaSelector,
  workfowIdSelector,
  (nmStps, crrntStp, alpha, wrkflId) => {
    switch (wrkflId) {
      case FINANCIAL_PLAN_WORKFLOW_ID:
        if (crrntStp < nmStps) {
          return Math.round(((crrntStp + alpha) / nmStps) * 100);
        }
        return 100;
      default:
        if (crrntStp < nmStps) {
          return Math.round(((crrntStp + alpha) / nmStps) * 20) * 5;
        }
        return 100;
    }
  },
);

export const hasLineSelector = createSelector(
  workfowIdSelector,
  currentStepSelector,
  (wrkflId, currentStep) => {
    switch (wrkflId) {
      case FINANCIAL_PLAN_WORKFLOW_ID:
        return true;
      case LARGE_PURCHASE_WORKFLOW_ID:
      case EDUCATION_WORKFLOW_ID:
      case HOME_PURCHASE_WORKFLOW_ID:
        return currentStep === 0;
      default:
        return false;
    }
  },
);

export const hideSelector = createSelector(
  workfowIdSelector,
  accountManualViewIdSelector,
  accountViewIdSelector,
  childrenViewIdSelector,
  pensionsViewIdSelector,
  debtsViewIdSelector,
  upsertViewSelector,
  (
    wrkflId,
    accountsManualViewID,
    accountsViewId,
    childrenViewId,
    pensionsViewId,
    debtsViewId,
    upsertView,
  ) => {
    switch (wrkflId) {
      case FINANCIAL_PLAN_WORKFLOW_ID:
        return (
          [ADD, EDIT].indexOf(accountsManualViewID) > -1 ||
          [UPSERT_VIEWS.LINK].indexOf(upsertView) > -1 ||
          [UPSERT_VIEWS.PLAID].indexOf(upsertView) > -1
        );
      case EDUCATION_WORKFLOW_ID:
      case LARGE_PURCHASE_WORKFLOW_ID:
        return false;
      case RETIREMENT_WORKFLOW_ID:
      case DEBT_MANAGEMENT_WORKFLOW_ID:
      case HOME_PURCHASE_WORKFLOW_ID:
      case EMERGENCY_FUND_WORKFLOW_ID:
        return (
          [ADD, EDIT].indexOf(debtsViewId) > -1 ||
          pensionsViewId === PENSIONS_CRUD_ADD_EDIT ||
          childrenViewId === CHILDREN_CRUD_ADD_EDIT ||
          [ADD, EDIT].indexOf(accountsViewId) > -1
        );
      default:
        return false;
    }
  },
);

export const labelSelector = createSelector(
  percentageSelector,
  workfowIdSelector,
  currentStepSelector,
  (percentage, wrkflId, currentStep) => {
    switch (wrkflId) {
      case FINANCIAL_PLAN_WORKFLOW_ID:
        return '';
      case LARGE_PURCHASE_WORKFLOW_ID:
      case EDUCATION_WORKFLOW_ID:
      case HOME_PURCHASE_WORKFLOW_ID:
        return currentStep === 0 ? '' : `${percentage}% Complete`;
      default:
        return `${percentage}% Complete`;
    }
  },
);
